import React from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@/pages/_post-opportunity/forms/Tooltip';
import classNames from 'classnames';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';

StringField.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  label: PropTypes.node,
  placeholder: PropTypes.string,
  description: PropTypes.node,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  error: PropTypes.node,
  optional: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  required: PropTypes.bool,
  tooltip: PropTypes.node,
  icon: PropTypes.elementType,
  shadow: PropTypes.bool,
  border: PropTypes.bool,
  focus: PropTypes.bool,
  inputType: PropTypes.string,
  minNumber: PropTypes.number,
  stepIncrement: PropTypes.number
};

StringField.defaultProps = {
  disabled: false
};

export default function StringField(props) {
  const {
    value,
    defaultValue,
    label,
    placeholder,
    description,
    disabled,
    onChange,
    error,
    inputRef,
    optional = false,
    required = false,
    tooltip,
    icon,
    shadow = true,
    border = true,
    focus = true,
    inputType = 'text',
    minNumber = 0,
    stepIncrement = 1,
  } = props;

  return (
    <div>
      {label && (
        <div className="relative flex space-x-0.5 text-sm text-cs-gray-500 mb-1">
          {required && (
            <span className="inline-block text-cs-orange-500">*</span>
          )}
          <span className="flex items-center space-x-1">
            <div className="text-base text-cs-gray-900 font-normal">
              {label}
            </div>
            {tooltip && (
              <div>
              <Tooltip tooltip={tooltip} label={true} />
              </div>
            )}
          </span>
          {optional && (
            <div className="absolute top-0 right-0 text-cs-gray-300 text-sm leading-6">
              Optional
            </div>
          )}
        </div>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <props.icon className="h-5 w-5 text-cs-gray-400" aria-hidden="true" />
          </div>
        )}
        <label aria-label={label}>
        <input
          ref={inputRef}
          type={inputType}
          min={inputType === 'number' ? minNumber : undefined}
          step={inputType === 'number' ? stepIncrement : undefined}
          className={classNames(
            icon && 'pl-10',
            shadow && 'shadow-cs-flat',
            focus && 'focus:ring-cs-blue-500 focus:border-cs-blue-500',
            'w-full py-2',
            border && '-m-px border rounded-md',
            border && !error && 'border-cs-gray-300',
            border && error && 'border-red-500',
            'placeholder:text-cs-gray-400 placeholder:font-normal text-cs-gray-900',
            disabled ? 'bg-cs-gray-100' : 'bg-white'
          )}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          readOnly={disabled}
          onChange={(event) => {
            onChange(event.target.value)
          }}
        />
        </label>
        {error && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
          </div>
        )}
      </div>
      {error ? (
        <div className="mt-2 text-sm text-red-500">
          {error}
        </div>
      ) : null}
      {description ? (
        <div className="text-sm text-cs-gray-500 font-normal mt-2">
          {description}
        </div>
      ) : null}
    </div>
  );
}
